import { Box } from "@chakra-ui/react";
import { ColorIcon, customEvent, Spacing } from "goi_common";
import { BaseButton, BaseStack, baseColors } from "goi_common/src/design-system";

import { useE5UrgentCall } from "../hooks/useE5UrgentCall";
import { ParlorDetailProps } from "@/types/parlor";
import { toParlorDisplayName } from "@/utils/parlorName";
import UrgentCallButton from "./UrgentCallButton";
import QuestionBottomCta, { QUESTION_BOTTOM_CTA_SPACE_VERTICAL } from "./QuestionBottomCta";

/**
 * 장례식장 견적 첫 화면 (E5). 질문 전에 이 장례식장 비용과 「맞춤 견적 후」 줄어들 수 있다는 것(금액은 ???)을 먼저 보여 준다.
 * 버튼을 누르면 「왜 조문객 수」 한 장 → 조문객 질문으로 넘어간다(2026-11-03~05 주완)
 */
export default function ParlorCostIntro({
  parlor,
  amountManwon,
  onStart,
}: {
  parlor: ParlorDetailProps;
  /**
   * 조문객 100명 · 3일장 예상 금액(만 원). 다음 화면들의 「100명」 범위(장례식장 공시 요금만, 서버 고정분 591만 원 제외)의
   * 한가운데 값 하나, 일의 자리는 0(2026-11-05 주완: 범위 말고 딱 중간 정도로). 계산이 안 되면 null(금액 상자를 그리지 않는다)
   */
  amountManwon: number | null;
  onStart: () => void;
}) {
  const parlorName = toParlorDisplayName(parlor.companyname);
  const amount = amountManwon !== null ? `약 ${amountManwon.toLocaleString()}만 원` : "";
  const onUrgent = useE5UrgentCall();

  return (
    <BaseStack width="100%" px="20px" pt="32px" bg={baseColors.background_normal}>
      {/* 운영 주체는 제목 위 작은 줄로(로고 + 이름). 장례식장 공식 페이지 오인 방지, 제목은 두 줄로 짧게(2026-11-03 주완) */}
      <BaseStack direction="horizontal" alignItems="center">
        <ColorIcon name="icon_Logo_GoiSignature" size={20} />
        <Spacing gap={6} orientation="horizontal" />
        <Box className="label_1_sb" color={baseColors.label_alternative_1}>
          고이장례연구소
        </Box>
      </BaseStack>
      <Spacing gap={10} />
      <BaseStack direction="horizontal" width="100%" alignItems="center">
        <Box
          as="h1"
          flex={1}
          className="title_3_b"
          color={baseColors.label_normal}
          wordBreak="keep-all"
          overflowWrap="anywhere"
        >
          <ParlorNameWithBreak name={parlorName} />
          <br />
          비용을 알려드릴게요
        </Box>
        <Spacing gap={8} orientation="horizontal" />
        <ColorIcon name="icon_Graphic_Savings" size={64} />
      </BaseStack>

      {amountManwon !== null && (
        <>
          <Spacing gap={20} />
          <BaseStack width="100%" p="20px" bg={baseColors.background_accent_1} borderRadius="12px">
            <Box className="label_1_r" color={baseColors.label_assistive_1}>
              조문객 100명 · 3일장 기준
            </Box>
            <Spacing gap={6} />
            {/* 제목(title_3_b 24px)보다 작게, 굵기는 볼드. 금액이 제목보다 크면 위계가 뒤집힌다(2026-11-03 주완) */}
            <Box className="headline_1_sb" fontWeight={700} color={baseColors.label_normal}>
              {amount}
            </Box>
            {/*
              누가 계산한 금액인지 밝힌다. 다른 장례식장이 「가격을 틀리게 썼다」 고 문제 삼을 때 근거가 되고,
              장례식장 공식 페이지가 아니라는 것도 경고문 없이 사실로 드러난다(2026-11-05 주완).
              「공식 아님」 같은 경고문은 전환을 떨어뜨렸다(#160, 광고 M-04) — 그래서 담백한 출처 한 줄.
              한 줄, 「고이가」, 출처 e하늘을 밝힌다(주완). 공시가 없는 항목은 전국 가운데값으로 채워 「공시 요금 등」
            */}
            <Spacing gap={8} />
            <Box className="caption_1_r" color={baseColors.label_assistive_1} whiteSpace="nowrap">
              e하늘 공시 요금 등으로 고이가 계산한 금액이에요
            </Box>
          </BaseStack>

          <>
            <Spacing gap={16} />
            <BaseStack
              width="100%"
              p="24px 20px 20px"
              bg={baseColors.background_accent_1}
              borderRadius="12px"
            >
              {/* 한 문장이라 두 줄을 같은 크기 · 굵기 · 색으로(18px 세미볼드, 2026-11-04 주완: 윗줄도 중요) */}
              <Box className="headline_2_sb" color={baseColors.label_normal}>
                {/* 「빈소 크기에 따라」 는 무엇을 말하는지 바로 안 읽혔다. 무엇을 비교하는지는 막대 아래(평형 · 일수)가 말한다(2026-11-04 주완) */}
                우리 가족 상황에 따라
              </Box>
              <Box className="headline_2_sb" color={baseColors.label_normal}>
                최대{" "}
                <Box as="span" color={baseColors.primary_orange}>
                  {HIDDEN_AMOUNT}만 원
                </Box>
                까지 아낄 수 있어요
              </Box>
              <Spacing gap={28} />
              <CompareBars beforeLabel={amount} />
            </BaseStack>
          </>
        </>
      )}

      <Spacing gap={QUESTION_BOTTOM_CTA_SPACE_VERTICAL} />
      {/* 디자인 시스템 Bottom CTA 세로형(메인 / 보조). 질문 화면과 같은 자리에 버튼을 둔다(2026-11-03 주완) */}
      <QuestionBottomCta>
        <BaseButton
          type="primary"
          size="large"
          onClick={() => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_cta" },
            });
            onStart();
          }}
        >
          <Box className="body_1_normal_m">우리 가족 기준 비용 계산하기</Box>
        </BaseButton>
        <Spacing gap={8} />
        <UrgentCallButton onClick={onUrgent} />
      </QuestionBottomCta>
    </BaseStack>
  );
}

/**
 * 첫 화면은 「견적 받기 전 / 맞춤 견적 후」 로 보여 준다(2026-11-04 주완).
 * - 위 박스 · 왼쪽 막대: 견적 받기 전 금액(실제 값)
 * - 오른쪽 막대(맞춤 견적 후) · 아낄 수 있는 금액: 「???」 로 가린다. 맞춤 견적을 받아야 알 수 있다는 궁금증을 남긴다
 * 위 박스를 세부 견적 후 금액으로 두면 가린 오른쪽 금액이 그대로 드러나서, 세부 견적 전 금액으로 맞췄다.
 * 금액 아래에 「e하늘 공시 요금 등으로 고이가 계산한 금액이에요」 출처를 밝힌다(다른 장례식장 문제 제기 대비, 11-05)
 */
const HIDDEN_AMOUNT = "???";

/** 이름이 길어 줄이 넘어가면 「장례식장」 앞에서 끊는다 (계명대학교대구동산병원 / 장례식장) */
const ParlorNameWithBreak = ({ name }: { name: string }) => {
  const matched = name.match(/^(.+?)(장례식장)$/);
  if (!matched) return <>{name}</>;
  return (
    <>
      {matched[1]}
      <wbr />
      {matched[2]}
    </>
  );
};

/** 「견적 받기 전」 막대 높이 */
const BAR_MAX_HEIGHT = 150;
/**
 * 「맞춤 견적 후」 막대 높이 비율. 실제 금액 비율로 그리면 차이가 안 보여서,
 * 줄어드는 게 한눈에 보이도록 고정 비율로 그린다(2026-11-03 주완: 과하지도 적지도 않게).
 * 비용 구성 화면의 「지금 예상 → 맞춤 견적 후」 막대(CompositionCard)도 같은 비율을 쓴다
 */
export const AFTER_BAR_RATIO = 0.64;

/**
 * 왼쪽 비교 대상(회색, 줄어드는 몫은 빗금) → 오른쪽 아끼는 쪽(주황).
 * 왼쪽에서 오른쪽으로 「전 → 후」 로 읽혀 결과(아낀 금액)에서 눈이 멈춘다(2026-11-04 주완)
 */
const CompareBars = ({ beforeLabel }: { beforeLabel: string }) => {
  const twoDayHeight = Math.round(BAR_MAX_HEIGHT * AFTER_BAR_RATIO);
  return (
    <BaseStack
      direction="horizontal"
      width="100%"
      justifyContent="center"
      alignItems="flex-end"
      spacing={32}
    >
      <BaseStack alignItems="center" width="110px">
        <BaseStack width="64px" height={`${BAR_MAX_HEIGHT}px`} borderRadius="8px" overflow="hidden">
          <Box
            height={`${BAR_MAX_HEIGHT - twoDayHeight}px`}
            background={`repeating-linear-gradient(135deg, ${baseColors.orange_150} 0 4px, ${baseColors.orange_100} 4px 8px)`}
          />
          <Box flex={1} bg={baseColors.gray_150} />
        </BaseStack>
        <BarLabel amount={beforeLabel} label="견적 받기 전" color={baseColors.label_assistive_1} />
      </BaseStack>
      <BaseStack alignItems="center" width="110px">
        <Box
          className="label_2_sb"
          color={baseColors.primary_orange}
          bg={baseColors.orange_100}
          px="8px"
          py="4px"
          borderRadius="6px"
        >
          -{HIDDEN_AMOUNT}만 원
        </Box>
        <Spacing gap={8} />
        <Box
          width="64px"
          height={`${twoDayHeight}px`}
          bg={baseColors.primary_orange}
          borderRadius="8px"
        />
        <BarLabel
          amount={`${HIDDEN_AMOUNT}만 원`}
          label="맞춤 견적 후"
          color={baseColors.primary_orange}
          emphasized
        />
      </BaseStack>
    </BaseStack>
  );
};

const BarLabel = ({
  amount,
  label,
  color,
  emphasized = false,
}: {
  amount: string;
  label: string;
  color: string;
  /** 「맞춤 견적 후」 금액은 크기는 같게, 굵기만 볼드로(2026-11-03 주완) */
  emphasized?: boolean;
}) => (
  <>
    <Spacing gap={12} />
    <Box className="body_1_normal_sb" fontWeight={emphasized ? 700 : undefined} color={color}>
      {amount}
    </Box>
    <Spacing gap={2} />
    <Box className="label_2_r" color={baseColors.label_assistive_1}>
      {label}
    </Box>
  </>
);
